import React, { useContext, useState } from 'react';
import dayjs from 'dayjs';
import { BsStars } from 'react-icons/bs';
import { HiOutlineUser, HiOutlineExclamationCircle, HiSelector, HiOutlineTrash } from 'react-icons/hi';
import { Avatar } from 'components/ui';
import { SessionContext } from 'contexts/SessionContext';
import { AIContext } from 'contexts/AIContext';
import Logo from 'components/template/Logo';
import MediaElement from 'views/calendar/MediaElement';
import { ImageChatContext } from 'contexts/ImageChatContext';

const ChatMessage = ({ 
    message, 
    aspectRatio, 
    onAspectRatioChange, 
    onDeleteMessage,
    isSelectable = false,
    handleOnSelect,
    handleOnUnselect,
    selectedImages = []
}) => {
    const isUser = message.type === 'user';
    const isError = message.type === 'error';
    const { user } = useContext(SessionContext);
    const { aiImageModels, aiSizeOptions } = useContext(AIContext);
    const { closeImageChat } = useContext(ImageChatContext);
    const [showAspectRatioSelector, setShowAspectRatioSelector] = useState(false);
    
    const getAspectRatioClass = (ratio) => {
        switch (ratio) {
            case '1:1': return 'aspect-square';
            case '16:9': return 'aspect-video';
            case '9:16': return 'aspect-[9/16]';
            case '4:3': return 'aspect-[4/3]';
            case '3:4': return 'aspect-[3/4]';
            case '3:2': return 'aspect-[3/2]';
            case '2:3': return 'aspect-[2/3]';
            default: return 'aspect-square';
        }
    };

    // Get available aspect ratios for the current model
    const getAvailableAspectRatios = () => {
        // For now, return all options. In a real implementation, you'd need to know which model is selected
        return aiSizeOptions;
    };

    if (isError) {
        return (
            <div className="flex justify-center">
                <div className="flex items-center gap-2 px-4 py-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl max-w-md">
                    <HiOutlineExclamationCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                    <span className="text-sm text-red-700 dark:text-red-300">
                        {message.text}
                    </span>
                </div>
            </div>
        );
    }

    return (
        <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
            <div className={`flex gap-3 max-w-[70%] w-full ${isUser ? 'flex-row-reverse' : 'flex-row'}`}>
                {/* Avatar */}
                <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                    isUser 
                        ? 'bg-gradient-to-br from-blue-500 to-purple-600' 
                        : 'bg-gradient-to-br from-purple-500 to-pink-600'
                }`}>
                    {isUser ? (
                        <Avatar 
                          size={35}
                          shape="circle"
                          src={user?.image?.url}
                          icon={<HiOutlineUser />}
                        />
                    ) : (
                        <div className="w-8 h-8 rounded-full overflow-hidden bg-gradient-to-br from-primary-800 to-primary-900 flex items-center justify-center">
                            <Logo 
                                type="streamline"
                                className="w-5 h-5"
                                imgClass="w-full h-full object-contain"
                            />
                        </div>
                    )}
                </div>

                {/* Message Content */}
                <div className={`flex flex-col gap-3 ${isUser ? 'items-end' : 'items-start'}`}>
                    {/* Text - only show for user messages */}
                    {isUser && message.text && (
                        <div className={`px-4 py-3 rounded-2xl w-auto max-w-lg ${
                            'bg-gradient-to-br from-blue-500 to-purple-600 text-white rounded-br-md'
                        }`}>
                            <p className="text-sm leading-relaxed whitespace-pre-wrap">
                                {message.text}
                            </p>
                        </div>
                    )}

                    {/* Aspect Ratio Selector - only show for non-user messages when there are images */}
                    {!isUser && onAspectRatioChange && (message.images || message.originalImages || message.generatedImages) && (
                        <div className="relative">
                            <button
                                onClick={() => setShowAspectRatioSelector(!showAspectRatioSelector)}
                                className="flex items-center gap-1 px-2 py-1 text-xs w-auto w-fit max-w-lg bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                            >
                                <HiSelector className="w-3 h-3" />
                                <span>{aspectRatio}</span>
                            </button>
                            
                            {showAspectRatioSelector && (
                                <div className="absolute top-full left-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-10 min-w-[120px]">
                                    {getAvailableAspectRatios().map((option) => (
                                        <button
                                            key={option.id}
                                            onClick={() => {
                                                onAspectRatioChange(option.id);
                                                setShowAspectRatioSelector(false);
                                            }}
                                            className={`w-full text-left px-3 py-2 text-xs hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors first:rounded-t-lg last:rounded-b-lg ${
                                                aspectRatio === option.id ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400' : 'text-gray-700 dark:text-gray-300'
                                            }`}
                                        >
                                            {option.label}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Images */}
                    {(message.images || message.originalImages || message.generatedImages) && (
                        <div className="space-y-3">
                            {/* Generated Images (for AI responses) */}
                            {message.generatedImages && !isUser && (
                                <div>
                                    <div className="flex justify-center">
                                        <div className="grid grid-cols-1 gap-2 w-full max-w-lg">
                                            {message.generatedImages.map((image) => (
                                                <div key={image.id} className="group relative w-full">
                                                    <MediaElement
                                                        id={image.id}
                                                        src={image.url}
                                                        originalSrc={image.url}
                                                        aspectRatio={aspectRatio}
                                                        isSelectable={isSelectable}
                                                        handleOnSelect={handleOnSelect}
                                                        handleOnUnselect={handleOnUnselect}
                                                        selected={selectedImages?.some(img => img.originalSrc === image.url)}
                                                        isEditable={false}
                                                        showPublishButton={!isSelectable}
                                                        isDraggable={false}
                                                        hideLayout={false}
                                                        className="w-full"
                                                        mediaDescription={message.text ? `Generated from: "${message.text}"` : undefined}
                                                        aiImageModel={message.aiModel}
                                                        user={message.user}
                                                        onPublishCallback={() => closeImageChat()}
                                                    />
                                                    {/* Delete button overlay - only show when not in selection mode */}
                                                    {onDeleteMessage && !isSelectable && (
                                                        <button
                                                            onClick={() => onDeleteMessage(message.id)}
                                                            className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-all transform hover:scale-110 bg-red-500 hover:bg-red-600 text-white p-2 rounded-lg shadow-lg z-[20]"
                                                            title="Delete this message and all following messages"
                                                        >
                                                            <HiOutlineTrash className="w-4 h-4" />
                                                        </button>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* User Images */}
                            {message.images && isUser && (
                                <div className="flex flex-wrap justify-end gap-2 max-w-lg">
                                    {message.images.map((image) => (
                                        <div key={image.id} className="group relative">
                                            <img
                                                src={image.url}
                                                alt="User uploaded"
                                                className={`object-contain h-[60px] rounded-xl border-2 border-gray-200 dark:border-gray-700 shadow-sm ${getAspectRatioClass(aspectRatio)}`}
                                            />
                                            <div className="absolute inset-0 bg-black/20 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity" />
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ChatMessage; 